import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Input,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'simx-calendar-input',
  standalone: false,
  templateUrl: './calendar-input.component.html',
  styleUrls: ['./calendar-input.component.scss'],
  host: {
    class: 'angular-component-wrapper',
  },
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: CalendarInputComponent,
    },
  ],
})
export class CalendarInputComponent implements ControlValueAccessor {
  private readonly _changeDetector = inject(ChangeDetectorRef);

  @Input('id') givenId: string = '';
  @Input('style') givenStyle: any = null;
  @Input('styleClasses') givenCssClasses: Array<string> = [];
  get givenCssClassesAsString() {
    return this.givenCssClasses.join(' ');
  }

  @Input('disabled') givenDisabled: boolean = false;
  _disabled: boolean = false;
  get disabled() {
    return this._disabled || this.givenDisabled;
  }

  @Input('icon') givenIcon: string = 'pi pi-calendar';
  @Input('title') givenTitle: string = '';
  @Input('showButtonBar') givenShowButtonBar: boolean = true;
  @Input('showExpired') givenShowExpired: boolean = true;
  @Input('showClear') givenShowClear: boolean = false;
  @Input('showIcon') givenShowIcon: boolean = true;
  @Input('showTime') givenShowTime: boolean = false;
  @Input('ariaLabel') givenAriaLabel: string =
    'Date in MM slash DD slash YYYY format';

  @Input('calendarOverlayWidth') calendarOverlayWidthOption:
    'auto' | 'constrain' = 'auto';

  @ViewChild('calendar', { read: ElementRef }) calendarElement!: ElementRef;
  calendarOverlayWidth: string = 'auto';

  touched: boolean = false;
  onTouched: Function = () => {};

  onChange: Function = (value: string) => {};

  value: string | null = '';

  writeValue(value: string): void {
    this.value = value;
    this._changeDetector.markForCheck();
  }

  registerOnChange(onChangeFunction: Function): void {
    this.onChange = onChangeFunction;
  }

  registerOnTouched(onTouchedFunction: Function): void {
    this.onTouched = onTouchedFunction;
  }

  markAsTouched(): void {
    if (!this.touched) {
      this.touched = true;
      this.onTouched();
    }
  }

  setDisabledState(disabled: boolean): void {
    this._disabled = disabled;
  }

  refreshDropdownOverlayWidth(): void {
    this.calendarOverlayWidth =
      this.calendarOverlayWidthOption === 'constrain' &&
      this.calendarElement &&
      this.calendarElement.nativeElement.children.length
        ? `${this.calendarElement?.nativeElement.children[0].clientWidth}px`
        : 'auto';
  }

  handleInputChange(event: any): void {
    this.value = event;
    this.onChange(event);
  }

  handleManualInputChange(event: any): void {
    const value = event.target.value;
    if (!value) {
      this.handleClear();
    } else {
      const dateValue = new Date(value);
      this.handleInputChange(dateValue);
    }
  }

  handleClear(): void {
    this.value = null;
    this.onChange(null);
  }

  handleShowDropdownOverlay(): void {
    this.refreshDropdownOverlayWidth();
  }

  handleIsExpired(): boolean {
    if (!this.value) {
      return false;
    }

    const value = new Date(this.value).setSeconds(0, 0);
    const now = new Date().setSeconds(0, 0);
    return now >= value;
  }
}
